import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { TextButton, Table } from '@shape-ds/components';
import { CASES_PAGE_LIST } from 'config/paths';
import useCaseModal from 'pages/Asset/tabs/Cases/hooks/useCaseModal';
import { SimpleAsset } from 'queries/assets/contract';
import { useAssetsByWorkspaceQuery } from 'queries/assets/queries';
import { CaseListItem, caseOpenStatus, caseTabs } from 'queries/cases/types';
import React, { Key, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { findByAssetNameRecursive } from 'utils/Trail';
import { getColumns } from './columns';
import './style.scss';
import AddNewCaseButton from 'pages/Asset/tabs/Cases/components/AddNewCaseButton/AddNewCaseButton';
import { OpenClosedCases } from 'pages/Asset/tabs/Cases/components/Filters/OpenClosedCases';
import CasesContextProvider, { useCaseContext } from 'pages/Asset/tabs/Cases/Cases.context';

export interface ListCasesProps {
  asset: SimpleAsset;
  maxHeight?: number;
  showTitle?: boolean;
}

const ListCases: React.FC<ListCasesProps> = ({ asset, maxHeight = 130, showTitle = false }) => {
  const { t } = useTranslation();
  const { data: treeData } = useAssetsByWorkspaceQuery();
  const { openCaseModal } = useCaseModal();

  const { filteredData, isFetching } = useCaseContext();

  const eventAssetTree =
    findByAssetNameRecursive(treeData?.items ?? [], asset.name)?.breadcrumbPath ?? [];

  const vesselId = eventAssetTree.reverse()?.[1]?.id || '';

  const handleOpenCasesPage = useCallback(() => {
    window.open(
      `${CASES_PAGE_LIST(caseTabs.all)}?equipment=${asset.name}&vesselId=${vesselId}`,
      '_blank',
    );
  }, [asset.name, vesselId]);

  const columns = useMemo(() => getColumns(), []);

  return (
    <div className="listcase-container" data-testid="listcase-container">
      {showTitle && <h2 className="listcase-title">{t('case:assetCases')}</h2>}

      <div className="listcase-actions">
        <OpenClosedCases />

        <div className="listcase-btn-wrapper">
          <TextButton
            onClick={handleOpenCasesPage}
            startIcon={<LaunchOutlinedIcon />}
            className="listcase-button"
          >
            <p data-testid="cases-page">{t('case:buttons.casesPage')}</p>
          </TextButton>

          <AddNewCaseButton data={{ asset }} className="listcase-new-button" />
        </div>
      </div>

      <Table<CaseListItem>
        style={{ maxHeight: maxHeight - 24 }}
        className="listcase-table"
        columns={columns}
        data={filteredData}
        isLoading={isFetching}
        onRowClick={(rowId: Key) => {
          const foundRow = filteredData.find((row) => row.id === rowId);
          foundRow && openCaseModal(foundRow);
        }}
      />
    </div>
  );
};

export default (listCasesProps: ListCasesProps) => (
  <CasesContextProvider
    defaultQueryFilters={{ assets_id: [listCasesProps.asset.id], activeTab: caseTabs.all }}
    defaultFilters={{
      status: caseOpenStatus,
    }}
  >
    <ListCases {...listCasesProps} />
  </CasesContextProvider>
);
